/**
 * Optional gyro/tilt input. On iOS 13+ `DeviceOrientationEvent.requestPermission`
 * must be called from a user gesture and the page must be HTTPS — so this is
 * gated behind an explicit `enable()` call wired to a tap. It is always a
 * *secondary* control: the joystick works whether or not tilt is granted.
 *
 * Reports a normalised vector in [-1, 1], corrected for landscape orientation.
 */
export interface DeviceTilt {
  readonly value: { x: number; y: number };
  /** True if this platform gates orientation access behind a permission prompt. */
  readonly needsPermission: boolean;
  /** Request permission (call from a user gesture) and start listening. */
  enable(): Promise<boolean>;
  dispose(): void;
}

interface OrientationPermissionCtor {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

export function createDeviceTilt(): DeviceTilt {
  const value = { x: 0, y: 0 };
  const ctor = window.DeviceOrientationEvent as unknown as OrientationPermissionCtor | undefined;
  const needsPermission = typeof ctor?.requestPermission === 'function';
  let listening = false;

  // Map beta/gamma to x/y, swapping for landscape. Tilt range ±30° → ±1.
  const RANGE = 30;
  const onOrient = (e: DeviceOrientationEvent) => {
    const beta = e.beta ?? 0; // front-back
    const gamma = e.gamma ?? 0; // left-right
    const angle = (typeof screen !== 'undefined' && screen.orientation?.angle) || 0;
    let ax: number;
    let ay: number;
    if (angle === 90 || angle === -90 || angle === 270) {
      // Landscape: device long-axis is horizontal, so beta drives x.
      ax = (angle === 90 ? beta : -beta) / RANGE;
      ay = (angle === 90 ? -gamma : gamma) / RANGE;
    } else {
      ax = gamma / RANGE;
      ay = beta / RANGE;
    }
    value.x = Math.max(-1, Math.min(1, ax));
    value.y = Math.max(-1, Math.min(1, ay));
  };

  function start() {
    if (listening) return;
    window.addEventListener('deviceorientation', onOrient);
    listening = true;
  }

  return {
    value,
    needsPermission,
    async enable() {
      try {
        if (needsPermission && ctor?.requestPermission) {
          const res = await ctor.requestPermission();
          if (res !== 'granted') return false;
        }
        start();
        return true;
      } catch {
        return false;
      }
    },
    dispose() {
      if (listening) window.removeEventListener('deviceorientation', onOrient);
      listening = false;
    },
  };
}
